/**
 * Utility to add scroll-to-close behavior for DevExtreme dropdown components
 * (SelectBox, Lookup, DropDownBox, TagBox, Autocomplete, DateBox)
 */

export interface DropdownInstance {
  hide?: () => void;
  close?: () => void;
  option?: (name: string) => any;
}

/**
 * Finds all scrollable containers that should trigger dropdown close
 * This includes:
 * - The SubPage's StyledFormWrapper (native scroll)
 * - DevExtreme ScrollView containers (.dx-scrollable-container)
 * - The main page Scroller container
 */
const findScrollableContainers = (): HTMLElement[] => {
  const containers: HTMLElement[] = [];

  // 1. Find the SubPage form wrapper - it's the child div inside .tmm-page-form with overflow-y: scroll
  //    The structure is: .tmm-page-form > ... > StyledFormWrapper (which has overflow-y: scroll)
  const formPage = document.querySelector('.tmm-page-form');
  if (formPage) {
    // Look for the scrollable div inside the form page
    // StyledFormWrapper gets overflow-y: scroll when isForm is true
    const scrollableDivs = formPage.querySelectorAll('div');
    scrollableDivs.forEach((div) => {
      const style = window.getComputedStyle(div);
      if (
        style.overflowY === 'scroll' ||
        style.overflowY === 'auto' ||
        style.overflow === 'scroll' ||
        style.overflow === 'auto'
      ) {
        // Check if this element actually has scrollable content
        if (div.scrollHeight > div.clientHeight) {
          containers.push(div as HTMLElement);
        }
      }
    });
  }

  // 2. Find DevExtreme ScrollView containers
  const dxScrollContainers = document.querySelectorAll('.dx-scrollable-container');
  dxScrollContainers.forEach((container) => {
    if (container.scrollHeight > container.clientHeight) {
      containers.push(container as HTMLElement);
    }
  });

  // 3. Find the main scroller's scrollable content
  const mainScroller = document.querySelector('.tmm-page .dx-scrollable-container');
  if (mainScroller && !containers.includes(mainScroller as HTMLElement)) {
    containers.push(mainScroller as HTMLElement);
  }

  return containers;
};

/**
 * Checks if an element is inside a DevExtreme dropdown/popup overlay
 */
const isInsideDropdownPopup = (element: EventTarget | null): boolean => {
  if (!element || !(element instanceof HTMLElement)) {
    return false;
  }

  // Check if the element or any of its parents is a dropdown popup
  // DevExtreme dropdowns use these classes for their overlay content
  const dropdownSelectors = [
    '.dx-overlay-content',
    '.dx-popup-content',
    '.dx-dropdowneditor-overlay',
    '.dx-dropdown-menu',
    '.form-dropdown-popup',
    '.cards-dropdown-popup',
  ];

  return dropdownSelectors.some((selector) => element.closest(selector) !== null);
};

/**
 * Creates dropDownOptions configuration that closes the dropdown when scrolling
 */
export const createScrollCloseDropdownOptions = (
  existingOptions?: Record<string, any>,
): Record<string, any> => {
  let cleanupFunctions: (() => void)[] = [];

  return {
    ...existingOptions,
    hideOnOutsideClick: true,
    wrapperAttr: {
      class: 'form-dropdown-popup',
      ...(existingOptions?.wrapperAttr || {}),
    },
    onShown: (e: { component: DropdownInstance }) => {
      if (existingOptions?.onShown) {
        existingOptions.onShown(e);
      }

      const scrollableContainers = findScrollableContainers();
      const dropdownComponent = e.component;

      const handleScroll = (event: Event) => {
        // Ignore scroll events that originate from inside the dropdown popup itself
        if (isInsideDropdownPopup(event.target)) {
          return;
        }

        // Try hide() first (for dropdowns), then close() (for some other components)
        if (dropdownComponent?.hide) {
          dropdownComponent.hide();
        } else if (dropdownComponent?.close) {
          dropdownComponent.close();
        }
      };

      // Attach scroll listeners to all found scrollable containers
      scrollableContainers.forEach((container) => {
        container.addEventListener('scroll', handleScroll, { passive: true });
        cleanupFunctions.push(() => {
          container.removeEventListener('scroll', handleScroll);
        });
      });

      // Also listen for window scroll as a fallback
      window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
      cleanupFunctions.push(() => {
        window.removeEventListener('scroll', handleScroll, { capture: true });
      });
    },
    onHidden: (e: { component: DropdownInstance }) => {
      if (existingOptions?.onHidden) {
        existingOptions.onHidden(e);
      }

      // Clean up all scroll listeners
      cleanupFunctions.forEach((cleanup) => cleanup());
      cleanupFunctions = [];
    },
  };
};

export const DROPDOWN_EDITOR_TYPES = [
  'SelectBox',
  'Lookup',
  'DropDownBox',
  'TagBox',
  'Autocomplete',
  'DateBox',
] as const;

export type DropdownEditorType = (typeof DROPDOWN_EDITOR_TYPES)[number];

export const isDropdownEditorType = (editorType?: string): editorType is DropdownEditorType => {
  return DROPDOWN_EDITOR_TYPES.includes(editorType as DropdownEditorType);
};
